import { Directive, OnDestroy, OnInit } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SupplierApiTesterService } from '../../supplier-api-tester.service';
import { SupplierApiCall, SupplierApiListQuery, SupplierApiPagination } from '../../supplier-api-tester.models';

export interface ApiTesterStatusFilter {
  value: string;
  label: string;
}

/** Limite maximale de l'API : les lignes d'un ticket ou d'une commande tiennent sur une seule page. */
export const ALL_LINES_QUERY: SupplierApiListQuery = { page: 1, limit: 100 };

/**
 * Liste paginée et filtrable par statut du parcours guidé (expressions de besoin, commandes).
 * Recharge la liste et revient dessus à chaque changement de clé API.
 */
@Directive()
export abstract class ApiTesterListBase<T> implements OnInit, OnDestroy {
  abstract readonly statusFilters: ApiTesterStatusFilter[];

  items: T[] = [];
  pagination: SupplierApiPagination | null = null;
  status = '';
  pageIndex = 0;
  pageSize = 10;
  loading = false;
  errorMessage: string | null = null;

  protected readonly subscription = new Subscription();

  protected constructor(protected readonly supplierApiTesterService: SupplierApiTesterService) {}

  protected abstract fetchList(query: SupplierApiListQuery): Observable<SupplierApiCall<T[]>>;

  /** Retour à la liste : chaque écran ferme ses vues de détail. */
  protected abstract closeDetail(): void;

  ngOnInit(): void {
    this.subscription.add(
      this.supplierApiTesterService.apiKey$.pipe(filter(apiKey => !!apiKey)).subscribe(() => {
        this.pageIndex = 0;
        this.backToList();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  backToList(): void {
    this.closeDetail();
    this.loadList();
  }

  loadList(): void {
    this.loading = true;
    this.fetchList({ page: this.pageIndex + 1, limit: this.pageSize, status: this.status || undefined }).subscribe(
      call => {
        this.loading = false;
        this.items = call.data ?? [];
        this.pagination = call.pagination;
        this.errorMessage = call.errorMessage;
      },
    );
  }

  onStatusChange(status: string): void {
    this.status = status;
    this.pageIndex = 0;
    this.loadList();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadList();
  }
}
